import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_PATHS } from '../constants/app-routes';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { LoginRequest, LoginResponse, UserDto } from '../models/auth.models';
import { isUnauthorized } from '../utils/api-error.util';

const EXPIRY_MARGIN_MS = 60_000;

function tokenExpired(token: string): boolean {
  const payload = token.split('.')[1];
  if (!payload) {
    return false;
  }
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = Number(JSON.parse(json).exp);
    return Number.isFinite(exp) && exp * 1000 <= Date.now() + EXPIRY_MARGIN_MS;
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userSignal = signal<UserDto | null>(this.readUser());
  private readonly tokenSignal = signal<string | null>(this.readInitialToken());

  private readonly expiredSignal = signal(false);

  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal());
  /** A última sessão caiu por vencimento do token, não por saída voluntária. */
  readonly sessionExpired = this.expiredSignal.asReadonly();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {
    this.hasLiveSession();
  }

  /** Confere o vencimento do token agora; um token vencido é descartado. */
  hasLiveSession(): boolean {
    const token = this.tokenSignal();
    if (!token) {
      return false;
    }
    if (tokenExpired(token)) {
      this.clearSession();
      this.expiredSignal.set(true);
      return false;
    }
    return true;
  }

  login(credentials: LoginRequest) {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, credentials)
      .pipe(tap((response) => this.persistSession(response)));
  }

  register(payload: {
    name: string;
    email: string;
    password: string;
    referralCode?: string;
    marketingConsent?: boolean;
  }) {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/public/passaporte/conta`, payload)
      .pipe(tap((response) => this.persistSession(response)));
  }

  patchUser(patch: Partial<UserDto>): void {
    const current = this.userSignal();
    if (!current) {
      return;
    }
    const next = { ...current, ...patch };
    this.userSignal.set(next);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(next));
  }

  logout(path: string = APP_PATHS.login): void {
    this.clearSession();
    this.expiredSignal.set(false);
    void this.router.navigateByUrl(path);
  }

  clearLocalSession(): void {
    this.clearSession();
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  getApiToken(): string | null {
    return this.tokenSignal();
  }

  loadProfile() {
    if (!this.getToken()) {
      return of(null);
    }

    return this.http.get<UserDto>(`${environment.apiBaseUrl}/auth/me`).pipe(
      tap((user) => {
        this.userSignal.set(user);
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
      }),
      catchError((err) => {
        if (isUnauthorized(err)) {
          this.logout();
          return of(null);
        }
        return of(this.userSignal());
      })
    );
  }

  private persistSession(response: LoginResponse): void {
    localStorage.setItem(STORAGE_KEYS.token, response.token);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(response.user));
    localStorage.removeItem(STORAGE_KEYS.demoMode);
    this.tokenSignal.set(response.token);
    this.userSignal.set(response.user);
    this.expiredSignal.set(false);
  }

  private clearSession(): void {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.demoMode);
    this.tokenSignal.set(null);
    this.userSignal.set(null);
  }

  private readInitialToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.token);
  }

  private readUser(): UserDto | null {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as UserDto;
    } catch {
      return null;
    }
  }
}
